import { forwardRef, Inject, ViewContainerRef, Injectable } from '@angular/core';
import {
  LMActionFormDialogResult,
  LMActionFormDialogResultCode,
  LMActionFormDialogService
} from '@modal/lm-action-form-dialog.service';
import { LMSpecService } from '@services/lm-spec.service';
import { LMErrorUtilService, LMFormatUtilService, LMUrlUtilService } from '@services/utils';
import { LMBusyIndicatorService, LMLoggerService, LMUrlInfoBuilderService } from '@app/services';
import { SohoToastService } from 'ids-enterprise-ng';
import * as urltemplate from 'url-template';
import { AppConstants } from '@utils/app.constants';
import { LMApplicationMenuTransactionService } from '@app/components';
import { LMNavigationService } from '@navigation/lm-navigation.service';
import { Subject } from 'rxjs';
import { LMGlobalUtilService } from '@app/utils';

@Injectable()
export class LMApplicationMenuSubMenuService {
  constructor(
    @Inject(forwardRef(() => SohoToastService)) private sohoToastService: SohoToastService,
    @Inject(forwardRef(() => LMBusyIndicatorService)) private lmBusyIndicatorService: LMBusyIndicatorService,
    @Inject(forwardRef(() => LMApplicationMenuTransactionService)) private lmApplicationMenuTransactionService: LMApplicationMenuTransactionService,
    @Inject(forwardRef(() => LMFormatUtilService)) private lmFormatUtilService: LMFormatUtilService,
    @Inject(forwardRef(() => LMNavigationService)) private lmNavigationService: LMNavigationService,
    @Inject(forwardRef(() => LMActionFormDialogService)) private lmActionFormDialogService: LMActionFormDialogService,
    @Inject(forwardRef(() => LMSpecService)) private lmSpecService: LMSpecService,
    @Inject(forwardRef(() => LMUrlUtilService)) private lmUrlUtilService: LMUrlUtilService,
    @Inject(forwardRef(() => LMUrlInfoBuilderService)) private lmUrlInfoBuilderService: LMUrlUtilService,
    @Inject(forwardRef(() => LMLoggerService)) private lmLoggerService: LMLoggerService,
    @Inject(forwardRef(() => LMGlobalUtilService)) private lmGlobalUtilService: LMGlobalUtilService,
    @Inject(forwardRef(() => LMErrorUtilService)) private lmErrorUtilService: LMErrorUtilService,
  ) {}

  public runAction(dialogUrl: string, dataView: LMDataView, placeholder: ViewContainerRef, pause$?: Subject<boolean>) {
    const actionName = this.lmUrlUtilService.getUrlParam(dialogUrl, 'action');

    if (!actionName) {
      this.lmLoggerService.log('unable to find action param in url');
      if (pause$) {
        pause$.next(false);
      }
      return null;
    }

    this.lmBusyIndicatorService.showBusyIndicator();

    this.lmSpecService.getFormSpec(dialogUrl).subscribe((formSpecResponse: LMFormSpecResponse) => {
      const formUrlInfo = formSpecResponse.formUrlInfo;
      const formSpec = formSpecResponse.formSpec;
      const actionSpec: LMActionSpec = formSpec.busClassSpec.actions ?
        formSpec.busClassSpec.actions.find(action => action.name === actionName) : formSpec.busClassSpec.actionSpec;

      this.lmApplicationMenuTransactionService.runMenuAction(formUrlInfo.transactionUrl, formSpec.busClassSpec, actionSpec, dataView, placeholder).subscribe(
        transactionState => {

          this.lmBusyIndicatorService.hideBusyIndicator();

          if (transactionState.isError) {
            this.lmErrorUtilService.handleError(
              transactionState.response.message,
              transactionState.response.details,
              transactionState.response.status,
              transactionState.response.message);
          } else if (transactionState.isCanceled) {
            // What to do?
          } else {
            const result: LMActionResult = transactionState.getTransactionResult();

            if (transactionState.isSuccess()) {
              const title = this.lmFormatUtilService.formatMessage(Soho.Locale.translate('ActionCompletedSuccessfully'), [ result.actionSpec.label ]);
              const message = result.message || Soho.Locale.translate('ActionSuccessful');
              this.lmGlobalUtilService.displayToastMessage(title, message, AppConstants.TRA_ACTION_COMPLETED);
            } else {
              this.lmErrorUtilService.handleError(
                result.message,
                result.details,
                result.status,
                result.message);
            }

            if (transactionState.actionFormDialogSaved && result.isCompletionNavigation && result.redirectUrl) {
              this.lmNavigationService.runNavigation(result.redirectUrl);
            }
          }

          if (pause$) {
            this.lmLoggerService.log([actionName, 'runAction from ApplicationMenu']);
            pause$.next(false);
          }
        });
    },
    error => {
      if (pause$) {
        this.lmLoggerService.log([actionName, 'runAction from ApplicationMenu']);
        pause$.next(false);
      }
    });
  }

  public requestActionFromPageMenu(dialogUrl: string, resourceId: string, placeholder: ViewContainerRef, pause$?: Subject<boolean>) {
    const actionName = this.lmUrlUtilService.getUrlParam(dialogUrl, 'action');

    if (!actionName) {
      this.lmLoggerService.log('unable to find action param in url');
      if (pause$) {
        pause$.next(false);
      }
      return null;
    }

    this.lmLoggerService.log([ actionName, 'Getting formspec for dialog']);
    this.lmSpecService.getFormSpec(dialogUrl).subscribe((response: LMFormSpecResponse | LMSystemException) => {
      this.lmLoggerService.trace([this, 'LMFormService.getFormSpec2() complete', response]);

      // todo dboehm spec-url-refactor how to return an error from here? - Phillip
      // if ((response as LMSystemException).status === AppConstants.STATUS_SYSTEM_ERROR) {
      //   const statusText = (response as LMSystemException).statusText;
      //   const details = (response as LMSystemException).details;
      //   this.lmErrorUtilService.handleError(statusText, details);
      //   return;
      // }

      const formUrlInfo = (response as LMFormSpecResponse).formUrlInfo;
      const formSpec = (response as LMFormSpecResponse).formSpec;
      const actionSpec: LMActionSpec = formSpec.busClassSpec.actions ?
        formSpec.busClassSpec.actions.find(a => a.name === actionName) : formSpec.busClassSpec.actionSpec;

      const urlCompiledTemplate = urltemplate.parse(formUrlInfo.initialDataUrl);
      const url = urlCompiledTemplate.expand({ resourceId, actionName });

      this.lmLoggerService.log([actionName, 'Getting dataview for dialog']);
      this.lmActionFormDialogService.getActionFormDialogDataView(url).subscribe((responseDV: any) => {
        const formDataView: LMDataView = responseDV;
        if (formDataView.status === AppConstants.STATUS_COMPLETED) {
          this.lmActionFormDialogService.runActionFormDialog(formUrlInfo, formSpec, actionSpec, formDataView)
            .subscribe((result: LMActionFormDialogResult) => {
              if (result.result === LMActionFormDialogResultCode.ACTION_FORM_DIALOG_SAVED) {
                const title = '';
                const message = result.transactionStateResult.completedTransactionList[ 0 ].message;
                this.lmGlobalUtilService.displayToastMessage(title, message, AppConstants.TRA_ACTION_COMPLETED);

                if (result.transactionStateResult.completedTransactionList[0].isCompletionNavigation && result.transactionStateResult.completedTransactionList[0].redirectUrl) {
                  this.lmNavigationService.runNavigation(result.transactionStateResult.completedTransactionList[0].redirectUrl);
                }
              }
            });
        } else {
          this.lmLoggerService.error(responseDV);
          this.lmErrorUtilService.handleError(responseDV.message, responseDV.details, responseDV.status, responseDV.statusText);
        }
        if (pause$) {
          this.lmLoggerService.log([actionName, 'Resume ApplicationMenu click']);
          pause$.next(false);
        }
      });
    },
    error => {
      if (pause$) {
        this.lmLoggerService.log([actionName, 'Resume ApplicationMenu click']);
        pause$.next(false);
      }
    });
  }
}
